<script setup>
import { WdsButtonLink } from '@wds/components';
import { DEFAULT_CURRENCY } from '~/config/currencies';

const props = defineProps({
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  averageCityPricesList: {
    type: Array,
    required: true,
  },
});
const { useUrl } = useUrls();
const { useImage } = useImages();
const MAX_NUM_CARDS = 9;
const propertyTypeObj = props.propertyTypeObj;
const expanded = ref(false);

// COMPUTED
const maxNumCards = computed(() => {
  return MAX_NUM_CARDS;
});

const hasAverageCityPrices = computed(() => {
  return props.averageCityPricesList?.length > 0;
});

// METHODS
function toggleExpanded() {
  expanded.value = !expanded?.value;
}

function getAverageCityPriceData(city) {
  const cityImage = city?.image?.small || city?.image?.mobile || null;
  const cityImageUrl = cityImage ? useImage.getImage(cityImage) : null;

  const locationData = {
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: city.urlFriendlyContinent,
    urlFriendlyCountry: city.urlFriendlyCountry,
    urlFriendlyCity: city.urlFriendlyName,
  };

  const avgCitySharedMinPrice = city?.avgDormPrice?.value || 0;
  const avgCityPrivateMinPrice = city?.avgPrivatePrice?.value || 0;
  const avgCityPriceCurrency = city?.avgDormPrice?.currency || city?.avgPrivatePrice?.currency || DEFAULT_CURRENCY.code;
  const avgCityPrices = [Number.parseFloat(avgCitySharedMinPrice), Number.parseFloat(avgCityPrivateMinPrice)].filter(
    (price) => price > 0,
  );

  return {
    name: city.name,
    image: cityImageUrl,
    location: city?.country || null,
    price: {
      value: Math.min(...avgCityPrices),
      currency: avgCityPriceCurrency,
    },
    link: useUrl.getCityPageUrl(locationData),
  };
}
</script>

<template>
  <section
    v-if="hasAverageCityPrices"
    class="average-city-prices-section"
  >
    <h2
      class="average-city-prices-heading"
      v-html="$t('t_AVERAGECITYPRICES')"
    />
    <div class="average-city-prices-list">
      <LazyCommonHwDestinationCard
        v-for="(city, index) in averageCityPricesList"
        v-show="index < maxNumCards || expanded"
        :key="city.id"
        source="average-city-prices"
        v-bind="getAverageCityPriceData(city)"
      />
    </div>
    <WdsButtonLink
      v-if="averageCityPricesList.length > maxNumCards"
      target="_blank"
      rel="noopener"
      class="average-city-prices-more-link"
      :text="$t(expanded ? 't_SHOWLESS' : 't_SHOWMORE')"
      :icon-end="expanded ? 'chevron-up' : 'chevron-down'"
      @click="toggleExpanded()"
      @keyup.enter="toggleExpanded()"
    />
  </section>
</template>

<style lang="scss" scoped>
.average-city-prices-section {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-l;

  .average-city-prices-heading {
    @include title-2-bld;

    color: $wds-color-ink-darker;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .average-city-prices-list {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-m;
  }

  .average-city-prices-more-link {
    place-self: flex-end flex-end;
  }

  @include desktop {
    gap: wds-rem(56px);
    padding: 0 wds-rem(40px);

    .average-city-prices-heading {
      @include title-1-bld;
    }

    .average-city-prices-list {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: $wds-spacing-l;
      width: 100%;
    }
  }
}
</style>
